import LandUsePlanningIcon from '@app/domains/useCases/components/LandUsePlanningIcon'
import type { UseCaseConfig } from '../types'

export const landUsePlanningAndDevelopment: UseCaseConfig = {
  id: 'land-use-planning-and-development',
  title: 'Land Use Planning and Development',
  tagline: "Design tomorrow's growth for tomorrow's conditions.",
  accentColor: 'sageGreen',
  iconComponent: LandUsePlanningIcon,
  roles: [
    'Strategic and spatial planners in government',
    'Policy planners developing district plan provisions',
    'Resource consenting and hearings roles',
    'Private development and planning consultancies',
    'Iwi and hapū planning roles',
  ],
  keyQuestions: [
    'How might changing hazards affect the places, infrastructure, and services that future communities will depend on over 20-, 50-, and 100-year horizons?',
    'Which locations, communities, and environmental or cultural values are most at stake under different growth patterns?',
    'What consequences could arise from development in these areas, including cascading impacts across infrastructure and services?',
    'Which locations remain suitable for development over time, and when might they transition to high or very high risk?',
    'How do alternative growth scenarios compare in terms of long-term risk, consequences, and exposure?',
    'What level of residual risk is acceptable, and how should that shape zoning, consenting, and infrastructure investment decisions?',
    'What evidence is required to support and defend these decisions as conditions, datasets, and expectations evolve?',
  ],
  relatedUseCaseIds: ['adaptation-planning', 'asset-and-infrastructure-planning'],
  forumUrl: 'https://forum.resilience-explorer.com/c/landuse',
  overviewBody:
    'Land use decisions determine where communities live, work, and invest for decades to come – and once established, these patterns are difficult and costly to reverse. Understanding which areas remain viable for growth, and under what conditions, requires consistent risk information across multiple hazards and future climate scenarios. In practice, hazard data is fragmented across separate studies, assessments focus on hazard presence rather than consequences, and growth options are rarely tested before decisions are made. The challenge is not simply identifying hazard-prone areas, it is understanding what is at stake, how consequences evolve over time, and which development decisions remain suitable as conditions change.',
  framingQuestion:
    'How do we make land use decisions today that remain defensible as hazard conditions change?',
  learningPathwayIntro:
    'Land use planning sits at the intersection of hazard science, policy, and community values. The articles below are organised to support that, from the regulatory framework and core risk concepts through to methods for testing growth scenarios, building defensible evidence, and engaging communities.',
  tutorials: [],
  articleGroups: [
    {
      title: 'NPS-NH framework and obligations',
      articles: [
        {
          title: 'The Planning Process for Risk',
          description:
            'How to scope and set up a risk assessment that will produce credible, decision-ready outputs – from problem definition and method selection to linking analysis to the MfE 10-step cycle.',
          href: '/app/guide/fundamentals/planning-and-implementation/the-planning-process-for-risk',
        },
        {
          title: 'Relevant Legislation for Climate Risk Assessment',
          description:
            'The international agreements and New Zealand statutes that underpin climate risk assessment obligations, from the Paris Agreement to the RMA, Climate Change Response Act, and Building Act.',
          href: '/app/guide/fundamentals/planning-and-implementation/relevant-legislation-for-climate-risk-assessment',
        },
      ],
    },
    {
      title: 'Risk concepts for planners',
      articles: [
        {
          title: 'What is risk',
          description:
            'Risk as the combination of consequences and uncertainty – the two-part definition that underpins the NPS-NH likelihood-consequence matrix.',
          href: '/app/guide/fundamentals/risk-consequences-and-uncertainty/what-is-risk',
        },
        {
          title: 'Hazards and Environmental Change',
          description:
            'An overview of hazard types and how climate change alters their frequency, intensity, and interactions.',
          href: '/app/guide/fundamentals/hazards-and-stressors/hazards-and-environmental-change',
        },
        {
          title: 'Understanding Return Periods',
          description:
            'How hazard probability is expressed through AEPs and return periods, and what those figures mean over planning timeframes.',
          href: '/app/guide/fundamentals/hazards-and-stressors/understanding-return-periods-and-ae-ps',
        },
      ],
    },
    {
      title: 'Growth scenario testing',
      articles: [
        {
          title: 'Dynamic Adaptive Pathways Planning (DAPP)',
          description:
            'How to build flexible adaptation strategies with signals and triggers that allow decisions to evolve as conditions change and new information emerges.',
          href: '/app/guide/fundamentals/decision-making-and-evaluation/dynamic-adaptive-pathways-planning',
        },
        {
          title: 'Real Options Analysis',
          description:
            'How to value flexibility in investment decisions, and when to defer, stage, or abandon development commitments under uncertainty.',
          href: '/app/guide/fundamentals/decision-making-and-evaluation/real-option-analysis',
        },
        {
          title: 'Robust Decision Making (RDM)',
          description:
            'How to identify strategies that perform acceptably across a wide range of possible futures, not just the most likely one.',
          href: '/app/guide/fundamentals/decision-making-and-evaluation/robust-decision-making',
        },
      ],
    },
    {
      title: 'Evidence and defensibility',
      articles: [
        {
          title: 'Strength of Knowledge',
          description:
            'A three-level scoring framework for assessing how well-founded the evidence behind a risk assessment is, and what weak or uncertain knowledge means for the confidence that can be placed in planning decisions.',
          href: '/app/guide/fundamentals/evidence-and-confidence/strength-of-knowledge',
        },
        {
          title: 'Sources of Uncertainty in Modelling',
          description:
            'Where modelling uncertainty comes from and how it compounds – what planners and consent officers need to understand when interpreting technical reports and deciding how much weight to place on model outputs.',
          href: '/app/guide/fundamentals/evidence-and-confidence/uncertainty-in-modelling',
        },
      ],
    },
    {
      title: 'Engagement and community values',
      articles: [
        {
          title: 'Community Values in Adaptation',
          description:
            'How to identify and document what communities value – the foundation for understanding what growth patterns put at stake and for demonstrating that planning decisions reflect genuine community input.',
          href: '/app/guide/fundamentals/values-participation-and-governance/community-values-in-adaptation',
        },
        {
          title: 'Engagement with Māori',
          description:
            'Te Tiriti obligations in spatial planning, and how to integrate mana whenua perspectives into growth decisions.',
          href: '/app/guide/fundamentals/values-participation-and-governance/engagement-with-maori',
        },
        {
          title: 'Heuristics and Cognitive Biases',
          description:
            'The mental shortcuts that lead planners, developers, and elected members to systematically underweight long-term or low-probability risks when making decisions.',
          href: '/app/guide/fundamentals/decisions-and-behaviour/heuristics-and-cognitive-biases',
        },
        {
          title: 'Why Numbers Matter in Hazard Communication',
          description:
            'How to explain return periods, probabilities, and likelihood classifications to communities, developers, and elected members who will make planning decisions based on them.',
          href: '/app/guide/insights/research-and-methods/why-numbers-matter-in-hazard-communication',
        },
      ],
    },
  ],
  dataNeedsIntro:
    'Understanding long-term suitability requires understanding how hazard conditions change over time, what consequences development may create, and how different growth, infrastructure, and planning decisions perform under future scenarios. The table below shows the four levels of capability, from identifying exposure through to evaluating long-term development strategies, and the minimum datasets required to support each level.',
  capabilityLevels: [
    {
      level: 1,
      title: 'Identify exposure',
      description: 'Identify which areas are exposed to natural hazards.',
    },
    {
      level: 2,
      title: 'Estimate risk',
      description: 'Assess the likelihood and consequences of development in those areas.',
    },
    {
      level: 3,
      title: 'Compare growth options',
      description:
        'Compare alternative locations and spatial patterns based on risk and consequence.',
    },
    {
      level: 4,
      title: 'Evaluate development strategies',
      description:
        'Test long-term growth, infrastructure, and mitigation strategies under future scenarios.',
    },
  ],
  dataRequirements: [
    {
      dataset: 'Extent (e.g. flood extent, coastal inundation, landslide)',
      category: 'Hazard',
      startLevel: 1,
    },
    {
      dataset: 'Intensity (e.g. flood depth, velocity, erosion rate)',
      category: 'Hazard',
      startLevel: 2,
    },
    {
      dataset: 'Likelihood (e.g. return periods)',
      category: 'Hazard',
      startLevel: 2,
    },
    {
      dataset: 'Scenarios (e.g. sea-level rise, rainfall change, future climate conditions)',
      category: 'Hazard',
      startLevel: 2,
    },
    {
      dataset: 'Land parcels or planning areas (e.g. parcels, zones, catchments)',
      category: 'Spatial',
      startLevel: 1,
    },
    {
      dataset: 'Existing land use and zoning (e.g. residential, industrial, rural)',
      category: 'Spatial',
      startLevel: 1,
    },
    {
      dataset: 'Infrastructure and services (e.g. transport, water, energy)',
      category: 'Spatial',
      startLevel: 2,
    },
    {
      dataset: 'Population (e.g. current or projected population)',
      category: 'Consequence and value',
      startLevel: 2,
    },
    {
      dataset: 'Buildings and development (e.g. existing buildings, development areas)',
      category: 'Consequence and value',
      startLevel: 2,
    },
    {
      dataset: 'Infrastructure dependency (e.g. reliance on roads, water, power)',
      category: 'Consequence and value',
      startLevel: 2,
    },
    {
      dataset: 'Environmental and cultural values (e.g. wetlands, heritage, iwi values)',
      category: 'Consequence and value',
      startLevel: 2,
    },
    {
      dataset: 'Value or economic proxies (e.g. land value, replacement value)',
      category: 'Consequence and value',
      startLevel: 3,
    },
    {
      dataset: 'Planning scenarios (e.g. growth areas, future demand, zoning scenarios)',
      category: 'Planning and scenarios',
      startLevel: 4,
    },
    {
      dataset: 'Development assumptions (e.g. density, staging, infrastructure provision)',
      category: 'Planning and scenarios',
      startLevel: 4,
    },
    {
      dataset: 'Mitigation measures (e.g. stopbanks, raised floor levels, retreat areas)',
      category: 'Planning and scenarios',
      startLevel: 4,
    },
    {
      dataset: 'Significant Natural Areas (SNAs)',
      category: 'Contextual and constraint layers',
      startLevel: 3,
    },
    {
      dataset: 'Outstanding Natural Features and Landscapes (ONFLs)',
      category: 'Contextual and constraint layers',
      startLevel: 3,
    },
    {
      dataset: 'Productive land classifications (e.g. LUC classes)',
      category: 'Contextual and constraint layers',
      startLevel: 3,
    },
    {
      dataset: 'Heritage sites and areas',
      category: 'Contextual and constraint layers',
      startLevel: 3,
    },
    {
      dataset: 'Coastal environment and MHWS boundaries',
      category: 'Contextual and constraint layers',
      startLevel: 3,
    },
    {
      dataset: 'Areas of significance to iwi and hapū',
      category: 'Contextual and constraint layers',
      startLevel: 3,
    },
  ],
}
